import React, { useContext, useEffect, useState } from "react";
import IOrder from "../../../models/IOrder";
import OwButton from "../OwButton/OwButton";
import IChRobinsonRatingRequestDTO from "../../../models/DTO/IChRobinsonRatingRequestDTO";
import { Socket } from "socket.io-client";
import IErrorDTO from "../../../models/DTO/IErrorDTO";
import UserContext from "../../../contexts/UserContext";
import SupplierContext from "../../../contexts/SupplierContext";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IOnChRobinsonRatingRequestDTO from "../../../models/DTO/IOnChRobinsonRatingRequestDTO";
import INotification from "../../../models/INotification";
import NotificationPositionEnum from "../../../models/NotificationPositionEnum";
import NotificationLevelEnum from "../../../models/NotificationLevelEnum";
import OwTitle from "../OwTitle/OwTitle";
import IChRobinsonQuoteSummaryItem from "../../../models/IChRobinsonQuoteSummaryItem";
import { ChRobinsonRatesModalSearchForm } from "./ChRobinsonRatesModalSearchForm/ChRobinsonRatesModalSearchForm";
import { ChRobinsonRatesModalListView } from "./ChRobinsonRatesModalListView/ChRobinsonRatesModalListView";
import IChRobinsonSaveRatingRequestDTO from "../../../models/DTO/IChRobinsonSaveRatingRequestDTO";
import * as _ from "lodash-es";

type Props = {
    weight: number;
    order: IOrder;
    closeModalFn: () => void;
    setActivityIndicator: (activityIndicator: boolean) => void;
    socket: Socket;
    notifyFn: (data: IErrorDTO | null, _notification?: any, isFormatted?: boolean) => void;
};

export const ChRobinsonRatesModal = (props: Props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);

    let ratingRequestItemsArr: IChRobinsonQuoteSummaryItem[] = [];

    if (props.order.Order_Detail?.chrobinson_rate_detail) {
        ratingRequestItemsArr = [props.order.Order_Detail?.chrobinson_rate_detail];
    }

    const [hasRatingsRequest, setHasRatingsRequest] = useState(!!ratingRequestItemsArr.length);
    const [ratingsRequestItems, setRatingsRequestItems] = useState<IChRobinsonQuoteSummaryItem[]>(ratingRequestItemsArr);
    const [selectedIndex, setSelectedIndex] = useState<number | undefined>();
    const [active, setActive] = useState(false);

    useEffect(() => {
        const sockets: { endpoint: EndpointsEnum; fn: any }[] = [
            { endpoint: EndpointsEnum.ChRobinsonOnRatingRequest, fn: onGetRatingRequest },
            { endpoint: EndpointsEnum.ChRobinsonOnSaveRatingRequest, fn: onSaveRatingRequest },
            { endpoint: EndpointsEnum.Notify, fn: notify },
        ];

        for (const socket of sockets) {
            props.socket.on(socket.endpoint, socket.fn);
        }

        return () => {
            for (const socket of sockets) {
                props.socket.removeListener(socket.endpoint, socket.fn);
            }
        };
    }, []);

    const notify = () => {
        setActive(false);
    };

    const getTransportOptions = (values: any) => {
        const payload: IChRobinsonRatingRequestDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            shipDate: values.shipDate,
            origin: {
                postalCode: values.origin,
            },
            weight: values.weight,
            destination: {
                postalCode: values.destination,
            },
            transportModes: [{ mode: values.transportMode }],
            customerCode: supplierModel.Supplier_Integration?.chrobinson_customer_code || "",
        };

        props.socket.emit(EndpointsEnum.ChRobinsonRatingRequest, payload);

        setActive(true);
    };

    const onGetRatingRequest = (data: IOnChRobinsonRatingRequestDTO) => {
        setHasRatingsRequest(true);
        setRatingsRequestItems(data.quoteSummaries);

        const notification: INotification = {
            message: "successfully_retrieved_rates",
            position: NotificationPositionEnum.bc,
            level: NotificationLevelEnum.success,
        };

        props.notifyFn(null, notification);

        setActive(false);
    };

    const selectRatingRequstIndex = (index: number) => {
        setSelectedIndex(index);
    };

    const saveRatingsRequestToOrder = () => {
        if (!_.isNil(selectedIndex)) {
            const payload: IChRobinsonSaveRatingRequestDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                rateDetail: ratingsRequestItems[selectedIndex],
                orderGuid: props.order.guid,
            };

            props.socket.emit(EndpointsEnum.ChRobinsonSaveRatingRequest, payload);
            setActive(true);
        } else {
            console.log("selectedIndex: ", selectedIndex);
        }
    };

    const onSaveRatingRequest = () => {
        const notification: INotification = {
            message: "successfully_saved_rates",
            position: NotificationPositionEnum.bc,
            level: NotificationLevelEnum.success,
        };

        props.notifyFn(null, notification);
        props.closeModalFn();
        setActive(false);
    };

    const restartRateSelection = () => {
        setHasRatingsRequest(false);
        setSelectedIndex(undefined);
    };

    return (
        <div className={"ChRobinsonRatesModal p-3"}>
            <div className={"d-flex justify-content-between align-items-center mb-4"}>
                <OwTitle title={"get_shipping_rates"} tag={"h5"} />
                <div>
                    <OwButton centerIconClass={"fas fa-times"} fn={props.closeModalFn} className={"btn-danger"} />
                </div>
            </div>
            <div>
                {hasRatingsRequest ? (
                    <ChRobinsonRatesModalListView
                        order={props.order}
                        items={ratingsRequestItems}
                        selectedIndex={selectedIndex}
                        selectRatingRequstIndex={selectRatingRequstIndex}
                        saveSelected={saveRatingsRequestToOrder}
                        restart={restartRateSelection}
                        active={active}
                    />
                ) : (
                    <ChRobinsonRatesModalSearchForm
                        weight={props.weight}
                        order={props.order}
                        getTransportOptions={getTransportOptions}
                        active={active}
                    />
                )}
            </div>
        </div>
    );
};
